import { ChangeEvent, useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tooltip,
  makeStyles,
  createStyles,
  Theme,
  IconButton,
  Fade,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { InfoOutlined } from "@material-ui/icons";
import { useClickAway } from "react-use";

import { Switch } from "./Switch";
import clsx from "clsx";

const _tooltipText =
  "Information on Sandbox vs Production mode. Suggest this tooltip is open on first start of application";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",

      padding: `${theme.spacing(2.5)}px ${theme.spacing(2)}px`,
    },
    text: {
      fontSize: theme.spacing(1.5),
      fontWeight: "bold",
      lineHeight: "16px",
      letterSpacing: 2,
      textTransform: "uppercase",

      color: theme.palette.coolGrey.main,

      transition: "all ease-in-out .3s",
    },
    mode: {
      marginRight: theme.spacing(0.5),
      color: "white",
    },
    tooltip: {
      padding: "14px 18px",

      fontWeight: "normal",
      fontSize: theme.spacing(1.25),
      lineHeight: 1.25,
      color: "white",

      backgroundColor: "#35353d",
      boxShadow: "0 10px 12px 0 rgba(51, 91, 130, 0.12), 0 0 1px 0 rgba(10, 31, 68, 0.1)",
    },
    icon: {
      padding: 0,

      marginRight: theme.spacing(1),

      color: theme.palette.coolGrey.main,

      "& svg": {
        fontSize: "16px",
      },
    },
    arrow: {
      color: "#35353d",
    },
  }),
);

type FooterProps = {
  isLoading: boolean;
  isProduction: boolean;
  tooltipText: string;
  onChange(e: boolean): void;
};

export const Footer = ({
  isLoading,
  isProduction,
  onChange,
  tooltipText = _tooltipText,
}: FooterProps) => {
  const [showTooltip, setShowTooltip] = useState(!window.localStorage.getItem("isVisited"));
  const classes = useStyles();
  const ref = useRef(null);

  useClickAway(ref, () => {
    if (showTooltip) setShowTooltip(false);
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (!window.localStorage.getItem("isVisited")) {
        window.localStorage.setItem("isVisited", "true");
      }
    }
  }, []);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="space-between" width="100%" className={classes.root}>
        <Skeleton height={19} width="50%" />
        <Skeleton variant="circle" height={24} width={24} />
      </Box>
    );
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.checked);
  };

  return (
    <Box className={classes.root}>
      <Box display="flex" alignItems="center">
        <Tooltip
          classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
          onClose={() => setShowTooltip(false)}
          onOpen={() => setShowTooltip(true)}
          TransitionComponent={Fade}
          placement="top-start"
          title={tooltipText}
          open={showTooltip}
          ref={ref}
          arrow
        >
          <IconButton className={classes.icon}>
            <InfoOutlined />
          </IconButton>
        </Tooltip>
        <Typography className={clsx(classes.text, classes.mode)}>
          {isProduction ? "production" : "sandbox"}
        </Typography>
        <Typography className={classes.text}>mode</Typography>
      </Box>
      <Switch checked={isProduction} onChange={handleChange} />
    </Box>
  );
};
